import React from 'react';
import { Button, Popconfirm } from 'antd';
// Types
import { FromToDatesFilter } from 'api/graphql-global-types';
// Hooks
import { useMarkCommissionsPaid } from 'components/common/AffiliateCommissionsReportTable/hooks/useMarkCommissionsPaid';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
import { formatCurrencyString } from 'helpers/utils';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

type MarkCommissionsPaidButtonProps = {
  userId: string;
  participantName: string;
  unpaidCommissionTotal: number;
  paidAtDateRange: FromToDatesFilter;
  onMarked: () => void;
};

const MarkCommissionsPaidButton = ({
  userId,
  participantName,
  unpaidCommissionTotal,
  paidAtDateRange,
  onMarked,
}: MarkCommissionsPaidButtonProps): JSX.Element => {
  const { markPaid, busy } = useMarkCommissionsPaid();

  const handleConfirm = async () => {
    try {
      const ordersCount = await markPaid({
        userId,
        paidAtDateRange,
        expectedUnpaidTotal: unpaidCommissionTotal,
      });

      successNotification(
        `Marked ${ordersCount} ${
          ordersCount === 1 ? 'order' : 'orders'
        } as paid`
      );
      onMarked();
    } catch (error) {
      errorNotification(
        getProperErrorMessage(
          error,
          (error as Error)?.message || 'Failed to mark the commissions as paid'
        )
      );
      // The unpaid total may have changed; reload so a retry sends the current one.
      onMarked();
    }
  };

  const hasUnpaid = unpaidCommissionTotal > 0;

  return (
    <Popconfirm
      title={`Mark ${formatCurrencyString(
        unpaidCommissionTotal
      )} owed to ${participantName} in this period as paid?`}
      onConfirm={handleConfirm}
      okText="Mark paid"
      cancelText="Cancel"
      disabled={!hasUnpaid || busy}
    >
      <Button type="link" disabled={!hasUnpaid} loading={busy}>
        Mark paid
      </Button>
    </Popconfirm>
  );
};

export default MarkCommissionsPaidButton;
